import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { AppDataProvider, useAppData } from './AppData';
import Backlog from './pages/Backlog';
import NuevaTarea from './pages/NuevaTarea';
import Revisiones from './pages/Revisiones';
import Usuarios from './pages/Usuarios';
import Dimensiones from './pages/Dimensiones';
import Dashboards from './pages/Dashboards';

type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'ugr.theme.preference.v3';
const LEGACY_THEME_STORAGE_KEYS = ['ugr.theme', 'ugr.theme.preference', 'ugr.theme.preference.v2'];

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  LEGACY_THEME_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
  return 'light';
}

function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => setTheme((cur) => (cur === 'dark' ? 'light' : 'dark'));

  return { theme, toggleTheme };
}

function Nav({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const { email, role, logout } = useAuth();
  const isAdmin = role === 'admin';
  const canBacklog = role === 'user' || role === 'admin';
  const canRevisiones = role === 'admin' || role === 'space_manager';
  const canDash = role === 'admin' || role === 'executive' || role === 'space_manager';
  return (
    <div className="nav">
      <span className="brand">
        <img src="/brand/ugr-logo-compact.svg" alt="" aria-hidden="true" />
        <span>Backlog UGR</span>
      </span>
      {canBacklog && <NavLink to="/backlog" className={({ isActive }) => (isActive ? 'active' : '')}>Backlog</NavLink>}
      {isAdmin && <NavLink to="/nueva" className={({ isActive }) => (isActive ? 'active' : '')}>Nueva tarea</NavLink>}
      {canRevisiones && <NavLink to="/revisiones" className={({ isActive }) => (isActive ? 'active' : '')}>Revisiones</NavLink>}
      {canDash && <NavLink to="/dashboards" className={({ isActive }) => (isActive ? 'active' : '')}>Dashboards</NavLink>}
      {isAdmin && <NavLink to="/usuarios" className={({ isActive }) => (isActive ? 'active' : '')}>Usuarios</NavLink>}
      {isAdmin && <NavLink to="/dimensiones" className={({ isActive }) => (isActive ? 'active' : '')}>Dimensiones</NavLink>}
      <span className="spacer" />
      <div className="identity">
        <button
          className="ghost sm theme-toggle"
          type="button"
          onClick={onToggleTheme}
          title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {theme === 'dark' ? '☀ Claro' : '☾ Oscuro'}
        </button>
        <div className="avatar" title={email}>{initials(email)}</div>
        <div className="who">
          <div className="name">{email.split('@')[0]}</div>
          <div className="role">{role}</div>
        </div>
        <button className="ghost sm" onClick={logout}>Salir</button>
      </div>
    </div>
  );
}

function initials(email: string): string {
  const local = (email.split('@')[0] || '').replace(/[._-]+/g, ' ').trim();
  const parts = local.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (local.slice(0, 2) || '?').toUpperCase();
}

function Home() {
  const { role } = useAppData();
  if (role === 'executive') return <Navigate to="/dashboards" replace />;
  if (role === 'space_manager') return <Navigate to="/revisiones" replace />;
  return <Navigate to="/backlog" replace />;
}

function Shell({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const canBacklog = role === 'user' || role === 'admin';
  const canRevisiones = role === 'admin' || role === 'space_manager';
  const canDash = role === 'admin' || role === 'executive' || role === 'space_manager';
  return (
    <AppDataProvider>
      <Nav theme={theme} onToggleTheme={onToggleTheme} />
      <div className="container">
        <Routes>
          <Route path="/" element={<Home />} />
          {canBacklog && <Route path="/backlog" element={<Backlog />} />}
          {isAdmin && <Route path="/nueva" element={<NuevaTarea />} />}
          {canRevisiones && <Route path="/revisiones" element={<Revisiones />} />}
          {canDash && <Route path="/dashboards" element={<Dashboards />} />}
          {isAdmin && <Route path="/usuarios" element={<Usuarios />} />}
          {isAdmin && <Route path="/dimensiones" element={<Dimensiones />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AppDataProvider>
  );
}

function Login() {
  const { passwordLogin, requestPasswordReset, error, notice } = useAuth();
  const [em, setEm] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const submit = async () => {
    setBusy(true);
    if (forgot) await requestPasswordReset(em);
    else await passwordLogin(em, pw);
    setBusy(false);
  };
  return (
    <div className="center">
      <div className="login-brand">
        <img src="/brand/ugr-logo-horizontal.svg" alt="Universidad del Gran Rosario" />
        <span>Backlog UGR</span>
        <p className="muted">Coordinación Comunicación y Marketing</p>
      </div>
      <div className="panel grid" style={{ width: 360 }}>
        <h2 style={{ margin: 0 }}>{forgot ? 'Restablecer contraseña' : 'Iniciar sesión'}</h2>
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          {forgot
            ? 'Te enviaremos un enlace al email registrado para elegir una contraseña nueva.'
            : 'Ingresá con tu email y contraseña.'}
        </p>
        <div><label htmlFor="login-email">Email</label><input id="login-email" type="email" autoComplete="email" value={em} autoFocus onChange={(e) => setEm(e.target.value)} placeholder="nombre@empresa.com" onKeyDown={(e) => e.key === 'Enter' && submit()} /></div>
        {!forgot && <div><label htmlFor="login-password">Contraseña</label><input id="login-password" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} /></div>}
        <button className="primary" disabled={busy || !em || (!forgot && !pw)} onClick={submit}>
          {busy ? 'Procesando…' : forgot ? 'Enviar enlace' : 'Ingresar'}
        </button>
        <button className="ghost" onClick={() => setForgot((value) => !value)}>
          {forgot ? 'Volver al inicio de sesión' : 'Olvidé mi contraseña'}
        </button>
        {notice && <div className="ok">{notice}</div>}
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}

function UpdatePassword() {
  const { updatePassword, error } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await updatePassword(password, confirmation);
    setBusy(false);
  };
  return (
    <div className="center">
      <div className="panel grid" style={{ width: 360 }}>
        <h2 style={{ margin: 0 }}>Elegir contraseña nueva</h2>
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>Usá al menos 12 caracteres.</p>
        <div><label htmlFor="new-password">Nueva contraseña</label><input id="new-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        <div><label htmlFor="confirm-password">Repetir contraseña</label><input id="confirm-password" type="password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} /></div>
        <button className="primary" disabled={busy || !password || !confirmation} onClick={submit}>{busy ? 'Guardando…' : 'Guardar contraseña'}</button>
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}

function SetInitialPassword() {
  const { setInitialPassword, error, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await setInitialPassword(password, confirmation);
    setBusy(false);
  };
  return (
    <div className="center">
      <div className="panel grid" style={{ width: 360 }}>
        <h2 style={{ margin: 0 }}>Elegí tu contraseña</h2>
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          Es tu primer ingreso. Por seguridad tenés que definir una contraseña propia
          (mínimo 12 caracteres) antes de continuar.
        </p>
        <div><label htmlFor="first-password">Nueva contraseña</label><input id="first-password" type="password" autoComplete="new-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        <div><label htmlFor="first-confirm">Repetir contraseña</label><input id="first-confirm" type="password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} /></div>
        <button className="primary" disabled={busy || !password || !confirmation} onClick={submit}>{busy ? 'Guardando…' : 'Guardar y continuar'}</button>
        {error && <div className="error">{error}</div>}
        <button className="ghost sm" onClick={logout}>Salir</button>
      </div>
    </div>
  );
}

function AccessDenied() {
  const { email, error, logout } = useAuth();
  return (
    <div className="center">
      <div className="error" style={{ maxWidth: 460 }}>
        <h2 style={{ marginTop: 0 }}>Acceso denegado</h2>
        <p>{error || `La cuenta ${email} no está autorizada.`}</p>
        <p>Contactá a un administrador del backlog.</p>
      </div>
      <button onClick={logout}>Salir</button>
    </div>
  );
}

export default function App() {
  const { theme, toggleTheme } = useTheme();
  const { user, role, loading, error, recoveryMode, mustChangePassword } = useAuth();
  if (loading) return <div className="center"><div className="muted">Cargando…</div></div>;
  if (recoveryMode) return <UpdatePassword />;
  if (!user) return <Login />;
  if (!role) return error ? <AccessDenied /> : <AccessDenied />;
  if (mustChangePassword) return <SetInitialPassword />;
  return <Shell theme={theme} onToggleTheme={toggleTheme} />;
}
